/**
 * Liste der Sound-Slots, die in einem Spielraum überschrieben werden können.
 * Jeder Raum kann pro Slot eine eigene Aufnahme hinterlegen. Fehlt eine
 * Aufnahme, fällt das Spiel auf die Default-Datei zurück.
 */
export const GAME_SOUND_KEYS = [
  "intro",
  "music",
  "jump",
  "shoot",
  "die",
  "monster",
  "rescue",
] as const;

export type GameSoundKey = (typeof GAME_SOUND_KEYS)[number];

export interface GameSoundMeta {
  key: GameSoundKey;
  /** Anzeigename für die UI */
  label: string;
  /** Kurzer Hilfetext, was der Spieler einsprechen soll */
  hint: string;
  /** Maximale Aufnahme-Länge in ms. Default ist `DEFAULT_MAX_SOUND_DURATION_MS`. */
  maxDurationMs?: number;
  /** Ob dieser Slot im Spiel geloopt werden soll (z. B. Hintergrundmusik). */
  loop?: boolean;
}

export const GAME_SOUND_META: Record<GameSoundKey, GameSoundMeta> = {
  intro: {
    key: "intro",
    label: "Intro",
    hint: "Begrüßung am Anfang des Levels (z. B. „Auf geht's, rettet die Princess!\").",
  },
  music: {
    key: "music",
    label: "Hintergrundmusik",
    hint: "Summe oder beatboxe deine Vibe – wird im Spiel in Endlosschleife abgespielt.",
    maxDurationMs: 20_000,
    loop: true,
  },
  jump: {
    key: "jump",
    label: "Sprung",
    hint: "Kurzer Schrei oder Geräusch beim Springen.",
  },
  shoot: {
    key: "shoot",
    label: "Schießen",
    hint: "Kurzer Ruf oder Effekt bei jedem Schuss (z. B. „Peng!\").",
  },
  die: {
    key: "die",
    label: "Tod",
    hint: "Reaktion auf das Ableben (z. B. „Aaaaargh!\").",
  },
  monster: {
    key: "monster",
    label: "Monster",
    hint: "Wenn man zum ersten Mal ein Monster sieht.",
  },
  rescue: {
    key: "rescue",
    label: "Rettung",
    hint: "Jubel beim Befreien der Princess.",
  },
};

/** Default-Aufnahme-Länge für Slots ohne expliziten Override (Millisekunden). */
export const DEFAULT_MAX_SOUND_DURATION_MS = 4000;

/**
 * @deprecated Nutze `maxDurationMsFor(key)` oder `DEFAULT_MAX_SOUND_DURATION_MS`.
 * Wird nur noch für Backward-Compat exportiert.
 */
export const MAX_SOUND_DURATION_MS = DEFAULT_MAX_SOUND_DURATION_MS;

/** Maximale Daten-Größe einer Aufnahme (Bytes). 1.5 MB reicht auch für 20 s opus. */
export const MAX_SOUND_BYTES = 1_500 * 1024;

/** Liefert die erlaubte Aufnahme-Länge für einen bestimmten Slot. */
export function maxDurationMsFor(key: GameSoundKey): number {
  return GAME_SOUND_META[key]?.maxDurationMs ?? DEFAULT_MAX_SOUND_DURATION_MS;
}

/**
 * Eine pro Raum hinterlegte Sprach-Aufnahme. `dataUrl` ist eine vollständige
 * `data:`-URL und kann direkt in `<audio src=…>` oder dem Phaser-Loader
 * verwendet werden.
 */
export interface CustomSound {
  key: GameSoundKey;
  dataUrl: string;
  mime: string;
  /** Größe der dekodierten Audio-Bytes in der `dataUrl`. */
  sizeBytes: number;
  /** Geschätzte Dauer in ms (Client liefert sie mit). */
  durationMs: number;
  recordedBy: {
    playerId: string;
    playerName: string;
    ts: number;
  };
}
